import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { MdInfo, MdRadar, MdOpenInNew } from 'react-icons/md';
import { Box, ClickableText, Flex, Heading, Icon, ProgressBar, Text } from 'core/components';
import LightweightDataViewWrapper from 'app/components/dataviews/LightweightDataViewWrapper';
import { addCommas } from 'app/util/utils';
import { COMPANY_MPS_QUERY } from 'app/views/metrics/queries/index.js';
import FilteredDevicesLink from 'app/components/links/FilteredDevicesLink';
import { PlanCardContainer, PlanCardLeftFlex, PlanCardMainContentWrapper, PlanIconBox } from './v2/layout';
import PlanCalloutWrapper from './v2/layout/PlanCalloutWrapper';

/**
 * Warning component displayed when device limits are reached or exceeded
 * Uses intent colors and built-in Callout styling
 */
const DeviceLimitWarning = ({ onContactAccountTeamClicked, overageLimitReached }) => (
  <PlanCalloutWrapper
    intent={overageLimitReached ? 'danger' : 'warning'}
    icon={overageLimitReached ? 'cross-circle' : 'warning-sign'}
  >
    <Text>You have reached your device limit.</Text>
    <ClickableText onClick={onContactAccountTeamClicked}>Please contact your account team.</ClickableText>
  </PlanCalloutWrapper>
);

@inject('$devices', '$metrics', '$setup')
@observer
class NmsDeviceUsageCard extends Component {
  render() {
    const {
      name: planName,
      id,
      description: planDescription,
      full_retention,
      device_count: deviceCount,
      max_devices,
      $setup,
      openContactAccountTeamDialog,
      ...rest
    } = this.props;

    const name = planName;
    const icon = (
      <Box
        width={40}
        height={40}
        borderRadius="8px"
        bg="primary"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Icon icon={MdRadar} iconSize={24} fill="white" />
      </Box>
    );
    const infoTipIcon = <Icon icon={MdInfo} iconSize={12} style={{ verticalAlign: 'middle', marginBottom: 1 }} />;
    const externalLinkIcon = <Icon icon={MdOpenInNew} iconSize={12} color="primary" />;

    // Device usage calculations - using local calculations instead of global MobX
    const currentDeviceCount = deviceCount; // Current number of devices in use
    const deviceLimitReached = currentDeviceCount >= max_devices; // Check if at or over plan limit
    const fullLicense = max_devices >= 100; // Check if plan is full license (100+ devices)
    // Overage calculations - devices beyond plan limit
    const overagesValue = currentDeviceCount > max_devices ? currentDeviceCount - max_devices : 0;
    const overageLimitReached = overagesValue >= 100; // Critical threshold: 100+ devices over limit
    return (
      <PlanCardContainer borderRadius={8} {...rest}>
        <PlanCardLeftFlex>
          <PlanIconBox>{icon}</PlanIconBox>
          <PlanCardMainContentWrapper>
            <Text color="muted" as="div" mb="4px" fontWeight="600" fontSize="14px">
              NMS
            </Text>
            <Heading level={4} mb={1}>
              {name}{' '}
              <Text fontWeight={200} muted>
                ({id})
              </Text>
            </Heading>
            <Text maxWidth={616} fontSize={12} color="muted" as="div" mb={1}>
              {planDescription}
            </Text>

            {currentDeviceCount === 0 && <Text color="muted">No devices have been added to this plan.</Text>}

            {currentDeviceCount > 0 && (
              <LightweightDataViewWrapper query={COMPANY_MPS_QUERY}>
                {({ loading }) => {
                  // Progress bar calculations
                  const progressVal = max_devices > 0 ? currentDeviceCount / max_devices : 0; // Main progress (0-1)
                  const overagesProgressVal = Math.min(overagesValue / 100, 1.0); // Overage progress (0-1, capped at 100)
                  return (
                    <Flex mt="4px" maxWidth="100%" flexWrap={['wrap', 'wrap', 'nowrap']}>
                      {/* Main device usage progress bar */}
                      <div style={{ flex: '1' }}>
                        <Box minWidth={deviceLimitReached && fullLicense ? 415 : 615} mb={1}>
                          <ProgressBar
                            value={progressVal}
                            animate={loading}
                            stripes={loading}
                            intent={deviceLimitReached ? 'warning' : 'primary'}
                            barBgColor="none"
                          />
                        </Box>
                        <Flex justifyContent="space-between" alignItems="center">
                          {loading && <Text color="muted">Loading...</Text>}

                          {!loading && (
                            <Flex
                              width={deviceLimitReached ? 'auto' : '100%'}
                              flexDirection={deviceLimitReached ? 'column' : 'row'}
                              justifyContent={deviceLimitReached ? 'flex-start' : 'space-between'}
                              alignItems={deviceLimitReached ? 'flex-start' : 'center'}
                            >
                              <Text style={{ marginTop: 4 }} fontWeight="bold">
                                Full-monitor Devices:
                              </Text>
                              <Text style={{ marginTop: 4 }} textAlign="left">
                                {addCommas(currentDeviceCount >= max_devices ? max_devices : currentDeviceCount)} of{' '}
                                {addCommas(max_devices)}
                              </Text>
                            </Flex>
                          )}
                        </Flex>
                      </div>
                      {/* overage progress bar - shown when device limit exceeded */}
                      {deviceLimitReached && fullLicense && (
                        <div style={{ marginLeft: 4, flex: '1' }}>
                          <Box mb={1}>
                            <ProgressBar
                              value={overagesProgressVal}
                              animate={loading}
                              stripes={loading}
                              intent="danger"
                              barBgColor="none"
                            />
                          </Box>
                          <Flex justifyContent="space-between" alignItems="center">
                            {loading ? (
                              <Text color="muted">Loading...</Text>
                            ) : (
                              <>
                                <Flex flexDirection="column" alignItems="flex-start">
                                  <Text fontWeight="bold">Overage {infoTipIcon}:</Text>
                                  <Text style={{ marginTop: 4 }} textAlign="left">
                                    {addCommas(overagesValue)} of 100
                                  </Text>
                                </Flex>
                              </>
                            )}
                          </Flex>
                        </div>
                      )}
                    </Flex>
                  );
                }}
              </LightweightDataViewWrapper>
            )}
          </PlanCardMainContentWrapper>
        </PlanCardLeftFlex>
        <Box>
          <Text color="muted" fontWeight="normal">
            Entitlements
          </Text>
          {/*  entitlements section */}
          <Flex mt={1} mb={1} flexDirection="column">
            {/* Full monitoring devices - premium monitoring with full feature set */}
            <Flex alignItems="center" justifyContent="space-between">
              <Text fontWeight="bold">
                Full-monitor Devices {infoTipIcon}:{' '}
                <Text as="span" fontWeight="normal">
                  {addCommas(max_devices)}
                </Text>
              </Text>
              <FilteredDevicesLink
                style={{ marginLeft: 8 }}
                filters={{ nmsConfigured: [true], isArchived: false }} // full monitoring devices that are configured
              >
                {externalLinkIcon}
              </FilteredDevicesLink>
            </Flex>
            <Flex alignItems="center" justifyContent="space-between" mt={1}>
              <Text fontWeight="bold">
                ICMP-Only Devices {infoTipIcon}:{' '}
                <Text as="span" fontWeight="normal">
                  {$setup.nmsIcmpOnlyDeviceCount} of Unlimited
                </Text>
              </Text>
              <FilteredDevicesLink
                style={{ marginLeft: 8 }}
                filters={{ nmsConfigured: '', isArchived: false, icmpStatus: ['up'] }} // icmp only devices that are up
              >
                {externalLinkIcon}
              </FilteredDevicesLink>
            </Flex>
          </Flex>

          <Text fontWeight="bold">
            Data Retention:{' '}
            <Text fontWeight="normal" as="span">
              {full_retention} Days
            </Text>
          </Text>

          {/*  limit warning - full and nonfull licenses */}
          {deviceLimitReached && (
            <DeviceLimitWarning
              onContactAccountTeamClicked={openContactAccountTeamDialog}
              overageLimitReached={overageLimitReached}
            />
          )}
        </Box>
      </PlanCardContainer>
    );
  }
}

export default NmsDeviceUsageCard;
export { DeviceLimitWarning };
